import { FieldKey } from '../../context/field-keys.js';
/**
 * SA8 — yield / GIY family, ported from
 * `records/controls/common/components/reactive-dynamic-table/vas-approved-reactive.component.ts`
 * (`setGiyMultiplierOnRowForm` ~552-610 and `updateAllGiyMultipliers` ~680-737):
 *
 *   GIY %        = passing_rent_amount_yearly / actual_sales_price × 100
 *   GIY-on-PGI % = potential_gross_income     / actual_sales_price × 100
 *   NIY %        = (passing_rent_amount_yearly − operating_expenses)
 *                    / (buyer_cost + actual_sales_price) × 100
 *   multiplier   = yield% ≠ 0 ? 100 / yield% : 0
 *
 * Modelled as pure PER-ROW rules: yield_type_desc / yield_percent / yield_multiplier are one
 * FormArray row's fields; the FormArray orchestration (iterating rows, subscriptions,
 * enable/disable of yield_percent for auto-calculated types, markDirty/markForCheck) stays host.
 *
 * Pinned legacy semantics:
 *  - every numeric input goes through `toNumberOrZero` (null/undefined/NaN/non-numeric -> 0);
 *  - yield-type match is case-insensitive (the row-edit variant lower-cases both sides; the
 *    FormArray variant compares exact strings — canonical dropdown values satisfy both);
 *  - when actual_sales_price is 0, or the type is not one of the three auto types, or the
 *    type's numerator is 0, the percent falls back to `toNumberOrZero(current yield_percent)`;
 *  - NO rounding and NO finite guard — legacy `setValue`s the raw quotient (an Infinity from a
 *    zero net-purchase-price flows through, and the multiplier of it becomes 0);
 *  - multiplier is 0 (not null) when the percent is 0.
 *
 * NOT ported (host-owned): the enable/disable of yield_percent per type, and SA9
 * `calculateGiyMultiplier` (dead code that contradicts its own docstring).
 */
/** Canonical yield_type_desc values (vas-approved-reactive.component.ts:49-51). */
export const GROSS_INITIAL_YIELD = 'Gross Initial Yield';
export const GIY_ON_PGI = 'GIY On PGI';
export const NET_YIELD = 'Net Yield';
/** Port of the component's `toNumberOrZero`: Number(value), non-finite -> 0. */
export function toNumberOrZero(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}
/**
 * Per-row yield percent kernel. Returns the auto-calculated percent for the three auto types,
 * otherwise `toNumberOrZero(currentPercent)` (also when actual_sales_price or the numerator is 0).
 */
export function saleYieldPercent(inputs) {
  const passingRent = toNumberOrZero(inputs.passingRentAmountYearly);
  const potentialGrossIncome = toNumberOrZero(inputs.potentialGrossIncome);
  const salesPrice = toNumberOrZero(inputs.actualSalesPrice);
  const buyerCost = toNumberOrZero(inputs.buyerCost);
  const operatingExpenses = toNumberOrZero(inputs.operatingExpenses);
  const netOperatingIncome = passingRent - operatingExpenses;
  const netPurchasePrice = buyerCost + salesPrice;
  const type = inputs.yieldType?.toLowerCase();
  if (salesPrice !== 0) {
    if (type === GROSS_INITIAL_YIELD.toLowerCase() && passingRent !== 0) {
      return passingRent / salesPrice * 100;
    }
    if (type === GIY_ON_PGI.toLowerCase() && potentialGrossIncome !== 0) {
      return potentialGrossIncome / salesPrice * 100;
    }
    if (type === NET_YIELD.toLowerCase() && netOperatingIncome !== 0) {
      return netOperatingIncome / netPurchasePrice * 100;
    }
  }
  return toNumberOrZero(inputs.currentPercent);
}
/** Multiplier kernel: 100 / yield% — 0 (not null) when the percent is 0. */
export function saleYieldMultiplier(yieldPercent) {
  const percent = toNumberOrZero(yieldPercent);
  return percent !== 0 ? 100 / percent : 0;
}
/** SA8: yield_percent from yield_type_desc + the sale amounts (per row). */
export const saleYieldPercentRule = {
  id: 'sale.yield-percent.from-type-and-amounts',
  inputs: [FieldKey.SaleYieldType, FieldKey.SaleYieldPercent, FieldKey.PassingRentAmountYearly, FieldKey.PotentialGrossIncome, FieldKey.SalePriceActual, FieldKey.BuyerCost, FieldKey.SaleOperatingExpenses],
  outputs: [FieldKey.SaleYieldPercent],
  formulaVersion: '1.0.0',
  run: ctx => {
    // Legacy writes the raw quotient (no formatNumber, no finite guard) — deps.round unused on purpose.
    ctx.set(FieldKey.SaleYieldPercent, saleYieldPercent({
      yieldType: ctx.get(FieldKey.SaleYieldType),
      currentPercent: ctx.get(FieldKey.SaleYieldPercent),
      passingRentAmountYearly: ctx.get(FieldKey.PassingRentAmountYearly),
      potentialGrossIncome: ctx.get(FieldKey.PotentialGrossIncome),
      actualSalesPrice: ctx.get(FieldKey.SalePriceActual),
      buyerCost: ctx.get(FieldKey.BuyerCost),
      operatingExpenses: ctx.get(FieldKey.SaleOperatingExpenses)
    }));
  }
};
/** SA8: yield_multiplier = 100 / yield_percent (0 when the percent is 0). */
export const saleYieldMultiplierRule = {
  id: 'sale.yield-multiplier.from-percent',
  inputs: [FieldKey.SaleYieldPercent],
  outputs: [FieldKey.SaleYieldMultiplier],
  formulaVersion: '1.0.0',
  run: ctx => {
    // Legacy writes the raw quotient — deps.round unused on purpose.
    ctx.set(FieldKey.SaleYieldMultiplier, saleYieldMultiplier(ctx.get(FieldKey.SaleYieldPercent)));
  }
};
export const saleYieldRules = [saleYieldPercentRule, saleYieldMultiplierRule];
